/**
 * React hook for real-time stat rate data and history.
 * Polls the StatTracker service at a configurable interval.
 */

import { useState, useEffect, useCallback } from 'react';
import { statTracker, RateSnapshot, HistoryPoint } from '@/systems/stats/StatTracker';

export interface StatRatesData {
  global: RateSnapshot;
  byZone: Record<string, RateSnapshot>;
  history: HistoryPoint[];
  historyByZone: Record<string, HistoryPoint[]>;
}

const EMPTY_SNAPSHOT: RateSnapshot = { fishPerHour: 0, currencyPerHour: 0 };

export function useStatRates(
  refreshIntervalMs: number = 5000,
  historySeconds: number = 60,
): StatRatesData {
  const [data, setData] = useState<StatRatesData>({
    global: EMPTY_SNAPSHOT,
    byZone: {},
    history: [],
    historyByZone: {},
  });

  const refresh = useCallback(() => {
    const global = statTracker.getSnapshot();
    // Union of currently-active zones and zones with recorded history.
    // This ensures zone charts persist after activity stops.
    const allZones = new Set([
      ...statTracker.getActiveZones(),
      ...statTracker.getZonesWithHistory(),
    ]);
    const byZone: Record<string, RateSnapshot> = {};
    const historyByZone: Record<string, HistoryPoint[]> = {};
    for (const zoneId of allZones) {
      byZone[zoneId] = statTracker.getSnapshot(zoneId);
      historyByZone[zoneId] = statTracker.getHistory(zoneId, historySeconds);
    }
    const history = statTracker.getHistory(undefined, historySeconds);
    setData({ global, byZone, history, historyByZone });
  }, [historySeconds]);

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, refreshIntervalMs);
    return () => window.clearInterval(id);
  }, [refreshIntervalMs, refresh]);

  return data;
}
